import { FormGroup } from '@angular/forms';
import { Observable, map } from 'rxjs';

import {
  ServiceRequestFormScheduledTechnician,
  ServiceRequestFormScheduledTechniciansResponse,
} from '@shared/models/service-request/service-request.model';
import { ServiceRequestService } from '@shared/services/service-request/service-request.service';
import { TechnicianSelectOption } from '@shared/utilities/technician-dropdown.util';
import {
  FieldStructureMap,
  INSPECTOR_LOOKUP_CONTROL_KEYS,
  STANDARD_INSPECTOR_QUESTION_ID,
} from './service-request-form-response.mapper.util';

export function resolveInspectorTechnicianFieldLabel(structure: FieldStructureMap): string {
  return structure['evaluationInspector']?.label
    ?? structure['technicianName']?.label
    ?? 'Inspector';
}

export interface ScheduledTechnicianDropdownOptions {
  clientId: string;
  serviceRequestId: number;
}

export function mapScheduledTechniciansToSelectOptions(
  technicians: ServiceRequestFormScheduledTechnician[],
): TechnicianSelectOption[] {
  return technicians.map((technician) => ({
    label: technician.technicianName,
    value: String(technician.technicianId),
  }));
}

export function normalizeScheduledTechniciansResponse(
  response: ServiceRequestFormScheduledTechniciansResponse,
): ServiceRequestFormScheduledTechnician[] {
  return response.data ?? [];
}

/** First scheduled technician on the SR — used for offline workspace prefetch. */
export function resolvePrimaryScheduledTechnicianId(
  technicians: ServiceRequestFormScheduledTechnician[],
): number | undefined {
  const match = technicians.find((technician) => technician.technicianId > 0);
  return match?.technicianId;
}

export function isTechnicianScheduledOnServiceRequest(
  technicians: ServiceRequestFormScheduledTechnician[],
  technicianId: number,
): boolean {
  if (!technicianId) {
    return false;
  }

  return technicians.some((technician) => technician.technicianId === technicianId);
}

export function fetchScheduledTechnicianOptions(
  serviceRequestService: ServiceRequestService,
  options: ScheduledTechnicianDropdownOptions,
): Observable<TechnicianSelectOption[]> {
  return serviceRequestService.getScheduledTechnicians({
    clientId: options.clientId,
    serviceRequestId: options.serviceRequestId,
  }).pipe(
    map((response) => mapScheduledTechniciansToSelectOptions(
      normalizeScheduledTechniciansResponse(response),
    )),
  );
}

export function buildScheduledTechniciansCacheKey(
  clientId: string,
  serviceRequestId: number,
): string {
  return `service-request-form:scheduled-technicians:${clientId}:${serviceRequestId}`;
}

export function isScheduledTechnicianLookupControlKey(controlKey: string): boolean {
  return (INSPECTOR_LOOKUP_CONTROL_KEYS as readonly string[]).includes(controlKey);
}

export function hasExternalInspectorTechnicianDropdownInStructure(
  structure: FieldStructureMap,
): boolean {
  return 'evaluationInspector' in structure;
}

export function resolveInspectorLookupFormValue(
  selected: { value?: string | number; label: string } | null,
): string {
  if (!selected) {
    return '';
  }

  const value = selected.value;
  if (value != null && String(value).trim() !== '') {
    return String(value).trim();
  }

  return selected.label?.trim() ?? '';
}

export function collectInspectorLookupControlValues(formGroup: FormGroup): string[] {
  return INSPECTOR_LOOKUP_CONTROL_KEYS
    .map((controlKey) => String(formGroup.get(controlKey)?.value ?? '').trim())
    .filter(Boolean);
}

export function resolveInspectorLookupDisplayLabel(
  formGroup: FormGroup,
  options: TechnicianSelectOption[],
  preferredControlKey?: string,
): string {
  const preferredValue = preferredControlKey
    ? String(formGroup.get(preferredControlKey)?.value ?? '').trim()
    : '';
  const allValues = collectInspectorLookupControlValues(formGroup);
  const candidates = preferredValue
    ? [preferredValue, ...allValues.filter((value) => value !== preferredValue)]
    : allValues;

  for (const candidate of candidates) {
    const label = resolveScheduledTechnicianLabel(candidate, options);
    if (label) {
      return label;
    }
  }

  return '';
}

export function resolveScheduledTechnicianLabel(
  rawValue: unknown,
  options: TechnicianSelectOption[],
): string {
  if (rawValue == null || String(rawValue).trim() === '') {
    return '';
  }

  const value = String(rawValue).trim();

  const byValue = options.find(
    (option) => String(option.value).toLowerCase() === value.toLowerCase(),
  );
  if (byValue?.label) {
    return byValue.label;
  }

  const byLabel = options.find(
    (option) => option.label.trim().toLowerCase() === value.toLowerCase(),
  );
  if (byLabel?.label) {
    return byLabel.label;
  }

  if (!/^\d+$/.test(value)) {
    return value;
  }

  return '';
}

export function resolveScheduledTechnicianId(
  rawValue: unknown,
  options: TechnicianSelectOption[],
): number | undefined {
  if (rawValue == null || String(rawValue).trim() === '') {
    return undefined;
  }

  const value = String(rawValue).trim();

  if (/^\d+$/.test(value)) {
    return Number(value);
  }

  const byLabel = options.find(
    (option) => option.label.trim().toLowerCase() === value.toLowerCase(),
  );
  if (byLabel?.value != null && String(byLabel.value).trim() !== '') {
    const parsed = Number(byLabel.value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

export function normalizeInspectorLookupControlValues(
  formGroup: FormGroup,
  options: TechnicianSelectOption[],
  controlKeys: readonly string[] = INSPECTOR_LOOKUP_CONTROL_KEYS,
): void {
  controlKeys.forEach((controlKey) => {
    const control = formGroup.get(controlKey);
    if (!control) {
      return;
    }

    const resolved = resolveScheduledTechnicianId(control.value, options);
    if (resolved != null) {
      control.setValue(String(resolved), { emitEvent: false });
    }
  });
}

export function resolveInspectorIdFromFormGroup(
  formGroup: FormGroup,
  options: TechnicianSelectOption[] = [],
): number | undefined {
  for (const controlKey of INSPECTOR_LOOKUP_CONTROL_KEYS) {
    const resolved = resolveScheduledTechnicianId(formGroup.get(controlKey)?.value, options);
    if (resolved != null) {
      return resolved;
    }
  }

  return undefined;
}

export function resolveInspectorIdFromDynamicForm(
  formGroup: FormGroup,
  questions: {
    formQuestionId: number;
    formQuestionType?: string;
    formQuestionLabel?: string;
  }[],
  options: TechnicianSelectOption[] = [],
): number | undefined {
  const inspectorQuestion = questions.find(
    (question) => question.formQuestionId === STANDARD_INSPECTOR_QUESTION_ID
      || (
        question.formQuestionType === 'USER_LOOK_UP'
        && question.formQuestionLabel?.trim().toLowerCase() === 'inspector'
      ),
  );

  if (!inspectorQuestion) {
    return undefined;
  }

  return resolveScheduledTechnicianId(
    formGroup.get(String(inspectorQuestion.formQuestionId))?.value,
    options,
  );
}

export function isInspectorTechnicianUserLookupQuestion(question: {
  formQuestionId: number;
  formQuestionType?: string;
  formQuestionLabel?: string;
}): boolean {
  if (question.formQuestionType !== 'USER_LOOK_UP') {
    return false;
  }

  if (question.formQuestionId === STANDARD_INSPECTOR_QUESTION_ID) {
    return true;
  }

  const label = question.formQuestionLabel?.trim().toLowerCase() ?? '';
  return label === 'inspector'
    || label === 'technician'
    || label.includes('sales / service technician name')
    || label.includes('sales/service technician name');
}

/** Copies the first populated inspector lookup value to sibling lookup controls. */
export function syncInspectorLookupControls(
  formGroup: FormGroup,
  controlKeys: readonly string[] = INSPECTOR_LOOKUP_CONTROL_KEYS,
): void {
  const sourceControlKey = controlKeys.find((controlKey) => {
    const value = String(formGroup.get(controlKey)?.value ?? '').trim();
    return value !== '';
  });

  if (!sourceControlKey) {
    return;
  }

  const sourceValue = String(formGroup.get(sourceControlKey)?.value ?? '').trim();
  propagateInspectorLookupSelection(formGroup, sourceControlKey, sourceValue, controlKeys);
}

export function propagateInspectorLookupSelection(
  formGroup: FormGroup,
  sourceControlKey: string,
  value: string,
  controlKeys: readonly string[] = INSPECTOR_LOOKUP_CONTROL_KEYS,
): void {
  if (!isScheduledTechnicianLookupControlKey(sourceControlKey)) {
    return;
  }

  controlKeys.forEach((controlKey) => {
    if (controlKey === sourceControlKey) {
      return;
    }

    const control = formGroup.get(controlKey);
    if (!control) {
      return;
    }

    control.setValue(value, { emitEvent: false });
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  });
}

export function propagateDynamicInspectorLookupSelection(
  formGroup: FormGroup,
  sourceQuestionId: number,
  value: string,
  questions: {
    formQuestionId: number;
    formQuestionType?: string;
    formQuestionLabel?: string;
  }[],
): void {
  const sourceQuestion = questions.find((question) => question.formQuestionId === sourceQuestionId);
  if (!sourceQuestion || !isInspectorTechnicianUserLookupQuestion(sourceQuestion)) {
    return;
  }

  questions.forEach((question) => {
    if (question.formQuestionId === sourceQuestionId) {
      return;
    }

    if (!isInspectorTechnicianUserLookupQuestion(question)) {
      return;
    }

    const control = formGroup.get(String(question.formQuestionId));
    if (!control) {
      return;
    }

    control.setValue(value, { emitEvent: false });
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  });
}

export function normalizeDynamicUserLookupControlValues(
  formGroup: FormGroup,
  questions: {
    formQuestionId: number;
    formQuestionType?: string;
  }[],
  options: TechnicianSelectOption[],
): void {
  questions.forEach((question) => {
    if (question.formQuestionType !== 'USER_LOOK_UP') {
      return;
    }

    const control = formGroup.get(String(question.formQuestionId));
    if (!control) {
      return;
    }

    const resolved = resolveScheduledTechnicianId(control.value, options);
    if (resolved != null) {
      control.setValue(String(resolved), { emitEvent: false });
    }
  });
}
